import { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import {
  ChatBubbleLeftRightIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { RecruiterEndorsementView } from '@type/endorsements';
import { cn } from '@utils/cn';

interface EndorsementCardProps {
  endorsement: RecruiterEndorsementView;
  /** "Title · Company" of the experience the reference is about. */
  experienceLabel?: string;
  /** Rendered at the bottom of the card (e.g. the candidate's hide toggle). */
  actions?: ReactNode;
  /** Dims the card — used for references the candidate hid from employers. */
  muted?: boolean;
}

const formatCollaborationPeriod = (from: string | null | undefined, to: string | null | undefined): string | null => {
  if (!from && !to) {
    return null;
  }
  const fmt = (d: string | null | undefined): string => {
    if (!d) {
      return '';
    }
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) {
      return d;
    }
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  };
  const start = fmt(from);
  const end = to ? fmt(to) : null;
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start || end || null;
};

export const EndorsementCard = ({
  endorsement, experienceLabel, actions, muted = false,
}: EndorsementCardProps) => {
  const { t } = useTranslation('common');
  const period = formatCollaborationPeriod(endorsement.collaborationFrom, endorsement.collaborationTo);
  const ChannelIcon = endorsement.sourceChannel === 'voice' ? PhoneIcon : ChatBubbleLeftRightIcon;

  return (
    <article className="rounded-md border border-surface-100 bg-white p-3">
      <div className={cn(muted && 'opacity-60')}>
        <header className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          {endorsement.endorserFullName && (
            <span className="text-sm font-semibold text-ink">
              {endorsement.endorserFullName}
            </span>
          )}
          <span className="rounded-full bg-surface-100 px-2 py-0.5 font-semibold uppercase tracking-wide text-ink-medium">
            {t(`endorsement.relation.${endorsement.relationType}`, endorsement.relationType.replace('_', ' '))}
          </span>
          {period && <span className="text-ink-medium">{period}</span>}
          <span className="ml-auto flex items-center gap-1 text-ink-medium" title={endorsement.sourceChannel}>
            <ChannelIcon className="size-3.5" />
          </span>
        </header>

        {experienceLabel && (
          <p className="mb-2 text-xs text-ink-medium">{experienceLabel}</p>
        )}

        <p className="whitespace-pre-line text-sm text-ink">
          {endorsement.summary}
        </p>

        {endorsement.strengths.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {endorsement.strengths.map((strength) => (
              <li key={strength} className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-ink">
                {strength}
              </li>
            ))}
          </ul>
        )}

        {endorsement.notableProjects && (
          <p className="mt-2 text-xs italic text-ink-medium">
            {t('endorsement.card.notable_projects_label', 'Notable projects:')} {endorsement.notableProjects}
          </p>
        )}

        {endorsement.wouldRecommend !== null && (
          <p className="mt-2 flex items-center gap-1 text-xs text-ink-medium">
            {endorsement.wouldRecommend
              ? (<HandThumbUpIcon className="size-3.5 text-secondary" />)
              : (<HandThumbDownIcon className="size-3.5 text-error" />)}
            {endorsement.wouldRecommend
              ? t('endorsement.card.would_recommend_yes', 'Would recommend')
              : t('endorsement.card.would_recommend_no', 'Would not recommend')}
          </p>
        )}
      </div>

      {actions && <div className="mt-3 border-t border-surface-100 pt-2">{actions}</div>}
    </article>
  );
};
